"use client"

/**
 * Opening the question panel from a row deep inside the paper.
 *
 * The roster renders as page → `StructureView` → `SectionCard` → row, and only
 * the page can own the panel: it is the surface the panel sits over, and the
 * row is re-rendered inside a drag context that should not carry overlay state.
 * Threading a callback through both middle layers would put a prop on two
 * components that have no use for it and two call sites that could forget it.
 *
 * Deliberately narrow. This carries one verb, and a row that is rendered
 * outside a provider simply is not clickable, which is the right outcome for
 * the catalog and docs stills that mount these rows with no panel behind them.
 */

import * as React from "react"

import type { QuestionRecord } from "@/src/pages/assessment-details"

const QuestionPreviewContext =
  React.createContext<((question: QuestionRecord) => void) | null>(null)

export function QuestionPreviewProvider({
  onPreview,
  children,
}: {
  onPreview: (question: QuestionRecord) => void
  children: React.ReactNode
}) {
  return (
    <QuestionPreviewContext.Provider value={onPreview}>
      {children}
    </QuestionPreviewContext.Provider>
  )
}

/** `null` when no panel is mounted above, so callers render plain text. */
export function useQuestionPreview() {
  return React.useContext(QuestionPreviewContext)
}
